import { HouseRepository } from 'lc-repository'
import houseService from '@/repository/services/v2/house'
import { constructReplica } from '@/helpers/search/constructReplica'
import { createDates } from '@/helpers/search/createDates'
import { createFacetFilters } from '@/helpers/search/createFacetFilters'
import { createNumericFilters } from '@/helpers/search/createNumericFilters'
import { findReplica } from '@/helpers/search/findReplica'
import { getBudget } from '@/helpers/search/getBudget'
import { handlePage } from '@/helpers/search/handlePage'
import { getDestinations } from '@/helpers/search/getDestinations'
import { isValidLat, isValidLng } from '@/helpers/search/geo'

import type { LocationQuery } from 'vue-router'
import type {
  InputSearchQuery,
  StateSearchBudgetFilters,
  StateSearchNumericFilter,
  StateSearchFacetFilter,
  StateSearchDateFilters,
  StateSearchReplicaFilters,
  StateSearchResults,
} from '@@/types/search/types'

export const useSearchInitStates = ({
  typeSearch,
}: {
  typeSearch: 'public' | 'admin' | 'b2b2c'
}) => {
  const config = useRuntimeConfig()
  const { $apiClient, $httpCookies, $lcRepositories } = useNuxtApp()
  const { locale, t } = useI18n()
  const { isMobile } = useDevice()
  const route = useRoute()
  const { currency } = useUser()
  const { data, setData } = useSearchInput()
  const { setBounds, resetBounds } = useSearchSplitMap()

  const stateSearchNumericFilter = useState<StateSearchNumericFilter>(
    'state-search-numeric-filter',
  )
  const stateSearchFacetFilter = useState<StateSearchFacetFilter>(
    'state-search-facet-filter',
  )
  const stateSearchDateFilters = useState<StateSearchDateFilters>(
    'state-search-date-filter',
  )
  const stateSearchBudgetFilters = useState<StateSearchBudgetFilters>(
    'state-search-budget-filter',
  )
  const stateSearchMultipleLocations = useState<InputSearchQuery[]>(
    'state-search-multiple-locations',
  )
  const stateSearchReplicaFilters = useState<StateSearchReplicaFilters>(
    'state-search-replica-filter',
  )
  const stateSearchResults = useState<StateSearchResults>(
    'state-search-results',
  )

  const fillStates = ({
    destinations,
    destination,
    queryOutput,
  }: {
    destinations?: InputSearchQuery[]
    destination?: { id: string; name: string; coords?: number[] }
    queryOutput: LocationQuery
  }) => {
    // Get replica
    const replicas = constructReplica({ config, userType: typeSearch })
    const replica = findReplica(replicas, route.query)

    // NumericFilters
    const { selectedNumericFilters } = createNumericFilters(route.query)

    // Date
    const { endDate, startDate } = createDates({
      query: route.query,
      $httpCookies,
    })

    // Price
    const { minBudget, maxBudget } = getBudget({
      query: route.query,
      userCurrency: currency.value,
    })

    // FacetFilters
    const { facetFilters } = createFacetFilters(queryOutput, locale)

    // Page
    const { page, showPreviousButton } = handlePage(route.query, isMobile)

    // Destination
    const destinationId =
      destination?.id && !isNaN(Number(destination?.id))
        ? (destination!.id as `${number}`)
        : null

    // Operated by LC (id is a number)
    if (destinationId) {
      setData('location', {
        id: destinationId,
        name: destination?.name,
        type: 'destination',
      })
      resetBounds()
    } else {
      const [neLat, neLng, swLat, swLng] = ['neLat', 'neLng', 'swLat', 'swLng']
        .map((param) => route.query[param]?.toString())
        .map(Number)

      const coordsAreValid =
        [neLat, neLng, swLat, swLng].every((n) => !isNaN(n!)) &&
        isValidLat(neLat!) &&
        isValidLng(neLng!) &&
        isValidLat(swLat!) &&
        isValidLng(swLng!)

      const [lat, lng] = ['lat', 'lng']
        .map((param) => route.query[param]?.toString())
        .map(Number)
      const centerPointIsValid =
        [lat, lng].every((n) => !isNaN(n!)) &&
        isValidLat(lat!) &&
        isValidLng(lng!)

      // From the map
      if (coordsAreValid) {
        setBounds([neLat!, neLng!, swLat!, swLng!])
        setData('location', {
          id: 'area',
          name: t('search.geographicArea'),
          type: 'destination',
        })
      }
      // From unknown destination
      else if (centerPointIsValid) {
        setData('location', {
          coords: [lat!, lng!],
          id: 'area_unknown',
          name: route.query.fullName?.toString() ?? data.value.location.name,
          type: 'destination',
        })
      } else {
        setData('location', {})
        resetBounds()
      }
    }

    const parsedStartDate = $dayjs(startDate.value, { format: 'YYYY-MM-DD' })
    const parsedEndDate = $dayjs(endDate.value, { format: 'YYYY-MM-DD' })

    setData('guest', selectedNumericFilters.capacity)
    setData(
      'startDate',
      parsedStartDate.isValid() ? parsedStartDate.toDate() : null,
    )
    setData('endDate', parsedEndDate.isValid() ? parsedEndDate.toDate() : null)

    stateSearchNumericFilter.value = {
      selectedNumericFilters: {
        bathrooms: selectedNumericFilters.bathrooms || 0,
        bedrooms: selectedNumericFilters.bedrooms || 0,
        capacity: selectedNumericFilters.capacity || 0,
      },
    }

    stateSearchFacetFilter.value = {
      facetFilters,
    }

    stateSearchDateFilters.value = {
      endDate: startDate.value && endDate.value ? endDate.value : null,
      startDate: startDate.value && endDate.value ? startDate.value : null,
    }

    stateSearchBudgetFilters.value = {
      maxBudget: maxBudget.value,
      minBudget: minBudget.value,
    }

    stateSearchMultipleLocations.value = destinations ?? []

    stateSearchReplicaFilters.value = {
      replicas: replicas.value,
      currentReplica: replica.value,
    }

    stateSearchResults.value.page = page.value
    stateSearchResults.value.meta.showPreviousButton = showPreviousButton.value
  }

  const fetchAndFillStates = async (destinationParams?: {
    id?: string
    name?: string
    queryOutput?: LocationQuery
  }) => {
    let destination:
      | { id: string; name: string; coords?: number[] }
      | undefined = undefined

    if (destinationParams?.id && destinationParams?.name) {
      destination = {
        id: destinationParams.id,
        name: destinationParams.name,
      }
    }

    if (
      route.query.destinationId &&
      data.value.location.name &&
      route.query.destinationId.toString() ===
        data.value.location?.id?.toString()
    ) {
      destination = {
        id: data.value.location.id,
        name: data.value.location.name,
      }
    } else if (
      route.query.destinationId &&
      route.query.destinationId.toString() !==
        data.value.location?.id?.toString()
    ) {
      const { data } = await HouseRepository(
        $lcRepositories.httpClient,
      ).getDestinationById({
        destinationId: route.query.destinationId.toString(),
      })

      if (data?.name)
        destination = {
          id: route.query.destinationId.toString(),
          name: data.name,
        }
    } else if (
      typeof data.value.location.id === 'string' &&
      data.value.location.id.startsWith('area') &&
      data.value.location.name
    ) {
      destination = {
        id: data.value.location.id,
        name: data.value.location.name,
      }
      if (
        data.value.location.id === 'area_unknown' &&
        data.value.location.coords?.length === 2
      )
        destination.coords = data.value.location.coords
    }

    // Rebuild the queryOutput to merge SEO facet-filters from destinationParams with route query
    const queryOutput = Object.entries(
      destinationParams?.queryOutput ?? {},
    ).reduce((acc, [key, val]) => {
      // Guard against duplicated values in route.query
      if (acc[key] && acc[key] === val) return acc
      // Specific usecase where a query value is a list (of facets)
      if (Array.isArray(val)) {
        if (!Array.isArray(acc[key])) acc[key] = []
        for (const v of val) {
          // We want all the unique facets (from path + query) in the queryOutput
          if (!acc[key].includes(v)) acc[key].push(v)
        }
        return acc
      }
      // Otherwise just add the key to the route query
      return {
        ...acc,
        [key]: val,
      }
    }, route.query)

    fillStates({
      destination,
      queryOutput,
    })
  }

  const fetchAndFillStatesMultiple = async (destinationParams?: {
    id: string
    name: string
  }) => {
    let destinations: { name: string; id: number }[] = []
    const arrayDestiIds =
      route.query.multipleDestinationsIds?.toString().split(',') || []
    const arrayHouseIds =
      route.query.multipleHousesIds?.toString().split(',') || []

    const allIds = [
      ...arrayDestiIds,
      ...(!arrayDestiIds.length &&
      !arrayHouseIds.length &&
      destinationParams?.id
        ? [destinationParams.id]
        : []),
    ]

    if (allIds.length) {
      destinations = (await getDestinations({
        $apiClient,
        destinationId: allIds,
        locale: locale.value,
      })) as { name: string; id: number }[]
    }

    const locationDestinations = destinations.map<InputSearchQuery>((d) => ({
      value: d.id,
      label: d.name,
      type: 'location',
    }))
    const housesDestination = await Promise.all(
      arrayHouseIds.map((id) => houseService($apiClient).read(id)),
    ).then((res) =>
      res.map<InputSearchQuery>(({ data }) => ({
        value: Number(data.id),
        label: data.name,
        type: 'type-of-house',
      })),
    )

    fillStates({
      queryOutput: route.query,
      destinations: [...locationDestinations, ...housesDestination],
      destination:
        !arrayDestiIds.length && !arrayHouseIds.length
          ? destinationParams
          : undefined,
    })
  }

  return {
    fetchAndFillStatesMultiple,
    fetchAndFillStates,
  }
}
